import { useMemo } from 'react';
import { CertControlById, QuestionStatusById } from '../app/store/questionnaireAnswerSlice';
import { Certification } from '../interfaces/certification.interface';
import { Questionnaire } from '../interfaces/questionnaire.interface';
import { BestPractice } from '../interfaces/bestPractice.interface';

export interface CodeAndBestPracticeType {
  code: string;
  bestPractice: BestPractice;
}

export interface QuestionWithCertsNeedingGeneration {
  answerIds: number[];
  certificates: Certification[];
  arCodes: CodeAndBestPracticeType[];
  bpCodes: CodeAndBestPracticeType[];
}

export type QuestionsNeedingCertAnswersMap = Record<string, QuestionWithCertsNeedingGeneration>;

export type QuestionsNeedingCertGenerationResult = {
  needsCertQARecords: QuestionsNeedingCertAnswersMap;
  hasNeedsCertQARecords: boolean;
};

export function useNeedsCertQARecords(
  questionStatus?: QuestionStatusById,
  certControlsById?: CertControlById,
  questionnaire?: Questionnaire,
): QuestionsNeedingCertGenerationResult {
  let hasNeedsCertQARecords = false;
  const needsCertQARecords = useMemo(() => {
    const result: Record<number, {
      answerIds: number[],
      certificates: Certification[],
      arCodes: CodeAndBestPracticeType[],
      bpCodes: CodeAndBestPracticeType[],
    }> = {};

    if (!questionStatus || !questionnaire) return result;

    for (const [id, qs] of Object.entries(questionStatus)) {
      if (qs?.status === 'cert' && !qs.questionAnswer) {
        const numId = Number(id);
        if (!Number.isNaN(numId)) {
          const certificates: Certification[] = [];
          const arCodes: string[] = [];
          const bpCodes: string[] = [];
          qs.question.excludedByCertifications.forEach((excludeId) => {
            if (certControlsById && certControlsById[String(excludeId)]) {
              certificates.push(certControlsById[String(excludeId)].certification);
            }
          });

          // Add short codes based on question type
          if (qs.question.isBestPractice) {
            bpCodes.push(qs.question.shortCode || '');
          } else {
            arCodes.push(qs.question.shortCode || '');
          }

          hasNeedsCertQARecords = true;
          const completeBp = questionnaire?.domains
            .find(d => d.id === (typeof qs.question.domain === 'number' ? qs.question.domain : qs.question.domain.id))?.topics
            .find(t => t.id === (typeof qs.question.topic === 'number' ? qs.question.topic : qs.question.topic.id))?.bestPractices
            .find(bp => bp.id === (typeof qs.question.bestPractice === 'number' ? qs.question.bestPractice : qs.question.bestPractice.id))!;

          result[numId] = {
            answerIds: qs.question.answers.map(a => a.id),
            certificates,
            arCodes: arCodes.map(code => ({
              code,
              bestPractice: completeBp,
            })),
            bpCodes: bpCodes.map(code => ({
              code,
              bestPractice: completeBp,
            })),
          };
        }
      }
    }
    return result;
  }, [questionStatus, certControlsById, questionnaire]);

  return {
    needsCertQARecords,
    hasNeedsCertQARecords,
  };
}
